import type { ISODateString, UUID } from './common';
import type { InvoiceKind, InvoiceSource } from './invoice';
import type {
  AgingBucket,
  InvoiceLifecycle,
  JobBillingState,
} from '../utils/invoiceLifecycle';
import type { InvoiceSendState } from '../utils/invoiceSendState';

export const WORKSPACE_STAGES = ['to_bill', 'drafts', 'sent', 'paid'] as const;
export type WorkspaceStage = (typeof WORKSPACE_STAGES)[number];

export const WORKSPACE_STAGE_LABELS: Record<WorkspaceStage, string> = {
  to_bill: 'To Bill',
  drafts: 'To Send',
  sent: 'Sent',
  paid: 'Paid',
};

/**
 * AgingBucket is NOT redeclared here — W1 already defines AGING_BUCKETS,
 * AGING_BUCKET_BOUNDS and agingBucket() in shared/utils/invoiceLifecycle.ts,
 * and both files are `export *`-ed from shared/index.ts. Redeclaring collides
 * on the barrel.
 */

/**
 * Applied across every stage (FR-2.3), so stage is NOT one of them — it is a
 * separate query param. Stage 1 lists jobs rather than invoices and therefore
 * consumes only the arms that mean something without an invoice: client, job
 * and search. Its date range is ignored, because an unbilled job has no
 * invoice_date to compare against (see the workspace service).
 */
export interface WorkspaceFilters {
  clientId?: UUID;
  jobId?: UUID;
  /** Matches invoice number, job code, job description and client name. */
  search?: string;
  dateFrom?: string;
  dateTo?: string;
}

/** Stage 1 rows are jobs. */
export interface ToBillRow {
  jobId: UUID;
  jobCode: string;
  jobDescription: string | null;
  clientId: UUID;
  clientName: string;
  billingState: JobBillingState;
  contractValue: number | null;
  invoiced: number;
  uninvoiced: number | null;
  /**
   * The job's completed date. There is no `job_card_ready_at` column — nothing
   * stamps when a job card reached 'ready' — and this is the honest stand-in:
   * it is what makes an unbilled row age, and it is what stage 1 sorts on.
   */
  completedDate: string | null;
}

/** Stages 2-4 rows are invoices. */
export interface WorkspaceInvoiceRow {
  invoiceId: UUID;
  invoiceNumber: string;
  jobId: UUID;
  jobCode: string;
  jobDescription: string | null;
  clientId: UUID;
  clientName: string;
  kind: InvoiceKind;
  /**
   * How the invoice got its figures. The action menu reads it to choose between
   * regenerating a line-item partial and editing a free-form one.
   */
  source: InvoiceSource;
  origin: 'native' | 'external';
  isCash: boolean;
  lifecycle: InvoiceLifecycle;
  amount: number;
  collected: number;
  outstanding: number;
  invoiceDate: string;
  /**
   * 'YYYY-MM-DD', derived server-side from the date the document bears (the sent
   * date once there is one, else the invoice date) and the invoice's payment
   * terms. Null when the invoice states no terms — those rows fall back to the
   * `overdue_after_days` rule for `isOverdue`.
   */
  dueDate: string | null;
  sentAt: ISODateString | null;
  daysOutstanding: number | null;
  agingBucket: AgingBucket | null;
  isOverdue: boolean;
  hasPdf: boolean;
  /** origin='external' && amount === 0 — a migrated receivable of unknown size (spec §5). */
  amountUnknown: boolean;
  notes: string | null;
  /**
   * The NEWEST send attempt's outcome (FR-4.6), so a bounce is visible on the
   * list without opening an inbox. Null when the invoice has never been sent.
   *
   * Optional because the same two fields are declared on BillingInvoiceRow,
   * which mocks and row factories construct by hand; a required field would
   * make every one of those a type error for no gain. Producers always set it.
   */
  lastSendState?: InvoiceSendState | null;
  /**
   * The provider's text for a bad outcome. Written whenever the provider sends
   * one, but only ever DISPLAYED behind isBadSendOutcome() — see that rule.
   */
  lastSendDetail?: string | null;
}

export type WorkspaceRow = ToBillRow | WorkspaceInvoiceRow;

export type WorkspaceCounts = Record<WorkspaceStage, number>;

export interface WorkspacePage<T> {
  rows: T[];
  total: number;
  page: number;
  pageSize: number;
}

export const WORKSPACE_PAGE_SIZE_DEFAULT = 50;
export const WORKSPACE_PAGE_SIZE_MAX = 100;
